import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  FileText,
  UserCheck,
  Gavel,
  Home,
  ClipboardList,
  Building2,
  CreditCard,
  ListChecks,
  Key,
  ArrowRight,
  TrendingUp,
} from 'lucide-react';
import { openChecklistPrintWindow } from '../../utility/openChecklistPrintWindow';
import { getDocumentById, updateDocument } from '../../utility/firebaseHelpers';
import { useAuth } from '../../auth/firebaseAuth';

// --- Types & Data Definitions (Kept the same) ---
export type ProgressState = {
  hasSolicitor: boolean;
  hasBER: boolean;
  hasTitleDeeds: boolean;
  hasPlanningDocs: boolean;
  hasComplianceCerts: boolean;
  hasLPTClearance: boolean;
  hasMortgageInfo: boolean;
  hasManagementInfo: boolean;
  hasFixturesList: boolean;
  hasKeysReady: boolean;
};

const legalItems = [
  {
    id: 'hasSolicitor',
    title: 'Instruct Your Solicitor',
    icon: <UserCheck className="h-6 w-6 text-blue-600" />,
    description: 'Your solicitor prepares contracts and manages conveyancing.',
    required: true,
  },
  {
    id: 'hasBER',
    title: 'BER Certificate',
    icon: <FileText className="h-6 w-6 text-blue-600" />,
    description: 'A valid BER is legally required to sell.',
    required: true,
  },
  {
    id: 'hasTitleDeeds',
    title: 'Title Deeds',
    icon: <Gavel className="h-6 w-6 text-blue-600" />,
    description: 'Proof of ownership and clear boundaries.',
    required: true,
  },
  {
    id: 'hasPlanningDocs',
    title: 'Planning Permission Documents',
    icon: <Home className="h-6 w-6 text-blue-600" />,
    description: 'Required for extensions or structural works.',
    required: false,
  },
  {
    id: 'hasComplianceCerts',
    title: 'Certificates of Compliance',
    icon: <ListChecks className="h-6 w-6 text-blue-600" />,
    description: 'Confirms compliance with planning and building regs.',
    required: false,
  },
];

const financialItems = [
  {
    id: 'hasLPTClearance',
    title: 'Local Property Tax Clearance',
    icon: <ClipboardList className="h-6 w-6 text-blue-600" />,
    description: 'LPT must be fully paid before completion.',
    required: true,
  },
  {
    id: 'hasMortgageInfo',
    title: 'Mortgage Redemption Figure',
    icon: <CreditCard className="h-6 w-6 text-blue-600" />,
    description: 'Provided by your lender if a mortgage exists.',
    required: false,
  },
  {
    id: 'hasManagementInfo',
    title: 'Management Company Information',
    icon: <Building2 className="h-6 w-6 text-blue-600" />,
    description: 'Required for apartments and managed estates.',
    required: false,
  },
  {
    id: 'hasFixturesList',
    title: 'Fixtures and Contents List',
    icon: <ListChecks className="h-6 w-6 text-blue-600" />,
    description: 'Clarifies what is included in the sale.',
    required: false,
  },
  {
    id: 'hasKeysReady',
    title: 'Keys and Access Ready',
    icon: <Key className="h-6 w-6 text-blue-600" />,
    description: 'All keys, fobs, and alarm codes available.',
    required: false,
  },
];

const allItems = [...legalItems, ...financialItems];

const defaultProgress: ProgressState = {
  hasSolicitor: false,
  hasBER: false,
  hasTitleDeeds: false,
  hasPlanningDocs: false,
  hasComplianceCerts: false,
  hasLPTClearance: false,
  hasMortgageInfo: false,
  hasManagementInfo: false,
  hasFixturesList: false,
  hasKeysReady: false,
};

const SELLER_REQUIRED = ['hasSolicitor', 'hasBER', 'hasTitleDeeds', 'hasLPTClearance'];

const BUYER_REQUIRED = [
  ...SELLER_REQUIRED,
  'hasPlanningDocs',
  'hasComplianceCerts',
  'hasMortgageInfo',
];

// --- Helper Component: Progress Header (NEW) ---
const ProgressHeader: React.FC<{
  progressPercent: number;
  completedCount: number;
  totalCount: number;
  isSellerReady: boolean;
  isBuyerReady: boolean;
}> = ({ progressPercent, completedCount, totalCount, isSellerReady, isBuyerReady }) => (
  <div className="bg-white p-6 rounded-xl shadow-lg border-b border-gray-100 mb-8 sticky top-0 z-10">
    <h1 className="text-3xl font-bold text-gray-900 mb-2">Selling Preparation Checklist</h1>

    {/* Progress Bar and Stats */}
    <div className="flex justify-between items-center mb-3">
      <p className="text-lg font-medium text-gray-700">
        Overall Progress:{' '}
        <span className="text-blue-600 font-semibold">
          {completedCount}/{totalCount} items
        </span>
      </p>
      <p className="text-2xl font-bold text-blue-600">{progressPercent}%</p>
    </div>

    {/* Actual Progress Bar */}
    <div className="w-full bg-gray-200 rounded-full h-2.5">
      <div
        className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
        style={{ width: `${progressPercent}%` }}
      ></div>
    </div>

    {/* Ready Statuses */}
    <div className="flex gap-4 mt-4">
      <span
        className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${
          isSellerReady ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}
      >
        {isSellerReady ? (
          <CheckCircle2 className="h-4 w-4 mr-1" />
        ) : (
          <ClipboardList className="h-4 w-4 mr-1" />
        )}
        Seller Ready: **
        {isSellerReady ? 'Ready to List' : 'REQUIRED Tasks Pending'}**
      </span>

      {/* Buyer Ready is secondary */}
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${
          isBuyerReady ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
        }`}
      >
        Buyer Ready: **{isBuyerReady ? 'Complete' : 'In Progress'}**
      </span>
    </div>
  </div>
);

// --- Main Component ---
const FSBOSellerDashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [progress, setProgress] = useState<ProgressState>(defaultProgress);
  const [loading, setLoading] = useState(true);

  // --- Data Fetching & State Logic (Kept the same) ---
  useEffect(() => {
    const fetchProfile = async () => {
      if (!currentUser) return;

      const userDoc = await getDocumentById('users', currentUser.uid);

      if (userDoc?.fsboProfiles?.progress) {
        setProgress({ ...defaultProgress, ...userDoc.fsboProfiles.progress });
      } else {
        await updateDocument('users', currentUser.uid, {
          fsboProfiles: {
            progress: defaultProgress,
            progressHistory: {},
          },
        });
      }

      setLoading(false);
    };

    fetchProfile();
  }, [currentUser]);

  const completedCount = useMemo(() => Object.values(progress).filter(Boolean).length, [progress]);

  const totalCount = allItems.length;

  const progressPercent = Math.round((completedCount / totalCount) * 100);

  const isSellerReady = SELLER_REQUIRED.every((id) => progress[id as keyof ProgressState]);

  const isBuyerReady = BUYER_REQUIRED.every((id) => progress[id as keyof ProgressState]);

  const handleToggle = async (id: string) => {
    if (!currentUser) return;

    const updatedProgress = {
      ...progress,
      [id]: !progress[id as keyof ProgressState],
    };

    const today = new Date().toISOString().split('T')[0];

    // Calculate new progress percent before updating the state
    const newCompletedCount = Object.values(updatedProgress).filter(Boolean).length;
    const newProgressPercent = Math.round((newCompletedCount / totalCount) * 100);

    const userDoc = await getDocumentById('users', currentUser.uid);

    const updatedHistory = {
      ...(userDoc?.fsboProfiles?.progressHistory || {}),
      // Use the calculated new percentage for history
      [today]: newProgressPercent,
    };

    setProgress(updatedProgress);

    await updateDocument('users', currentUser.uid, {
      fsboProfiles: {
        progress: updatedProgress,
        progressHistory: updatedHistory,
      },
    });
  };

  const handlePrint = () => {
    openChecklistPrintWindow({
      progress,
      allItems,
      isSellerReady,
      isBuyerReady,
      progressPercent,
    });
  };

  // --- Render Functions (IMPROVED UI/UX) ---
  const renderSection = (title: string, items: typeof legalItems) => (
    <div className="mb-8">
      <h2 className="text-2xl font-semibold text-gray-900 border-b pb-2 mb-4">{title}</h2>

      <div className="space-y-4">
        {items.map((item) => {
          const isCompleted = progress[item.id as keyof ProgressState];
          return (
            // Item Card: Apply background and border based on completion status
            <div
              key={item.id}
              className={`flex items-center p-4 rounded-lg shadow-sm border transition-all duration-300 cursor-pointer ${
                isCompleted
                  ? 'bg-green-50 border-green-200 opacity-80'
                  : 'bg-white border-gray-200 hover:shadow-md'
              }`}
              onClick={() => handleToggle(item.id)}
            >
              {/* Icon - With background change */}
              <div className={`p-3 rounded-full ${isCompleted ? 'bg-green-200' : 'bg-blue-50'}`}>
                {item.icon}
              </div>

              <div className="ml-4 flex-1">
                <h3
                  className={`font-semibold text-lg ${isCompleted ? 'text-green-800' : 'text-gray-900'}`}
                >
                  {item.title}
                  {/* Required Tag - More visible pill */}
                  {item.required && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                      REQUIRED
                    </span>
                  )}
                </h3>
                <p className={`text-sm ${isCompleted ? 'text-green-700' : 'text-gray-600'}`}>
                  {item.description}
                </p>
              </div>

              {/* Checkbox component */}
              <div className="ml-4 flex items-center">
                <input
                  type="checkbox"
                  checked={isCompleted}
                  onChange={() => handleToggle(item.id)}
                  className={`h-6 w-6 rounded border-2 cursor-pointer ${
                    isCompleted
                      ? 'border-green-500 bg-green-500 text-white'
                      : 'border-gray-300 text-blue-600 focus:ring-blue-500'
                  }`}
                  onClick={(e) => e.stopPropagation()} // Prevent double-toggling
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <span className="text-gray-500">Loading your dashboard...</span>
      </div>
    );
  }

  return (
    <div className="relative w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-visible min-h-screen  bg-gray-50 pt-8 pb-20 px-4">
      <div className="max-w-3xl mx-auto">
        {/* 1. Progress Header Component */}
        <ProgressHeader
          progressPercent={progressPercent}
          completedCount={completedCount}
          totalCount={totalCount}
          isSellerReady={isSellerReady}
          isBuyerReady={isBuyerReady}
        />

        {/* 2. Checklist Body */}
        <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
          {renderSection('Legal Requirements', legalItems)}
          {renderSection('Financial and Administration', financialItems)}
        </div>

        {/* 3. Call-to-Action Footer (Sticky on Mobile, fixed placement on Desktop) */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-xl p-4 md:static md:p-0 md:mt-10">
          <div className="max-w-3xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Secondary Actions */}
            <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
              <button
                onClick={handlePrint}
                className="w-full md:w-auto px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
              >
                Print Checklist for Solicitor
              </button>

              <button
                onClick={() => navigate('/home-valuation')}
                className="w-full md:w-auto inline-flex items-center justify-center px-5 py-2 rounded-lg border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 transition"
              >
                <TrendingUp className="mr-2 h-4 w-4" />
                Get Home Valuation
              </button>
            </div>

            {/* Primary Action: Listing */}
            <button
              disabled={!isSellerReady}
              onClick={() => navigate('/add-listing')}
              // Using a hypothetical 'bg-primary' for better branding
              className={`w-full md:w-auto inline-flex items-center justify-center px-6 py-3 rounded-lg text-white font-semibold transition-colors duration-200 ${
                !isSellerReady
                  ? 'bg-red-500 cursor-not-allowed shadow-md' // Visually block when not ready
                  : 'bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/50' // Use blue-600 as 'primary'
              }`}
            >
              {!isSellerReady ? 'Complete Required Steps to List' : 'Start Listing Process'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </div>

          {!isSellerReady && (
            <p className="md:hidden mt-2 text-center text-sm text-red-600">
              You must complete all **REQUIRED** items to continue.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FSBOSellerDashboard;
